import { useState } from "react";
import CustomInput from "../../components/common/Inputs/CustomInput";
import Modal from "../../components/common/Modal";
import { FormikHelpers, useFormik } from "formik";
import { CategoryInterface, TCategoryFilter } from "../../interfaces/categoryInterface";
import { usePagination } from "../../components/common/data-table/hooks/usePagination";
import { ListSearchControls } from "../../components/common/data-table/ListSearchControls";
import { ListViewControls } from "../../components/common/data-table/ListViewControls";
import Button from "../../components/common/Buttons/Button";
import { faCheck, faEdit, faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  categorySchema,
  useCategoriesFilter,
  useSaveCategory,
  useCategoryStatusToggle,
  useCategoryByIdQuery,
  CategoryTable,
  CategoryHeader,
} from "../../features/categories";

const formDataInit: CategoryInterface = {
  name: "",
  active: true,
};

const filterInit: TCategoryFilter = {};

const CategoryPage = () => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(
    null,
  );
  const { pagination, handlePageChange, handlePageSizeChange, handleSort } =
    usePagination({
      initialPageSize: 10,
      initialSortBy: "name",
      initialSortOrder: "desc",
    });

  const [searchTerm, setSearchTerm] = useState("");
  const [filter, setFilter] = useState<TCategoryFilter>(filterInit);

  const handleSearch = () => {
    setFilter({ ...filterInit, search: searchTerm || undefined });
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilter(filterInit);
  };

  const { categories, paginationData, isLoading } = useCategoriesFilter({
    filter,
    pagination,
  });

  const { data: categoryToEdit } = useCategoryByIdQuery(
    editingCategoryId ?? undefined,
  );

  const { handleSave } = useSaveCategory();
  const { handleEnable, handleDisable } = useCategoryStatusToggle();

  const formik = useFormik<CategoryInterface>({
    initialValues: categoryToEdit?.data || formDataInit,
    validationSchema: categorySchema,
    enableReinitialize: true,
    onSubmit: (
      values: CategoryInterface,
      formikHelpers: FormikHelpers<CategoryInterface>,
    ) => {
      handleSave(values);
      formikHelpers.setSubmitting(false);
      formikHelpers.resetForm({ values: formDataInit });
      handleCloseModal();
    },
  });

  const handleOpenNewCategoryModal = () => {
    formik.resetForm({ values: formDataInit });
    setEditingCategoryId(null);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    formik.resetForm({ values: formDataInit });
    setEditingCategoryId(null);
    setShowModal(false);
  };

  const handleEditCategory = (category: CategoryInterface) => {
    const { _id: categoryId } = category;
    if (!categoryId) return;
    setEditingCategoryId(categoryId);
    setShowModal(true);
  };

  return (
    <div>
      <CategoryHeader />
      <ListSearchControls
        searchLabel="Buscar por nombre"
        searchValue={searchTerm}
        onSearchChange={setSearchTerm}
        onSearch={handleSearch}
        onClear={handleClearSearch}
      />
      <ListViewControls
        pageSize={pagination.pageSize}
        onPageSizeChange={handlePageSizeChange}
      >
        <Button size="medium" onClick={handleOpenNewCategoryModal} color="primary">
          Nuevo
        </Button>
      </ListViewControls>
      <CategoryTable
        data={categories}
        isLoading={isLoading}
        sortBy={pagination.sortBy as keyof CategoryInterface}
        sortOrder={pagination.sortOrder}
        onSort={handleSort}
        pagination={{
          currentPage: paginationData?.currentPage ?? 1,
          totalPages: paginationData?.totalPages ?? 0,
          pageSize: paginationData?.pageSize ?? 10,
          total: paginationData?.total ?? 0,
          onPageChange: handlePageChange,
        }}
        extraActions={[
          {
            title: "Editar",
            icon: faEdit,
            actionKey: "edit",
            onClick: handleEditCategory,
          },
          {
            title: "Deshabilitar",
            icon: faXmark,
            actionKey: "disable",
            color: "secondary",
            onClick: (row) => handleDisable(row._id!),
            shouldShow: (row) => !!row.active,
          },
          {
            title: "Habilitar",
            icon: faCheck,
            actionKey: "enable",
            color: "success",
            onClick: (row) => handleEnable(row._id!),
            shouldShow: (row) => !row.active,
          },
        ]}
      />
      <Modal show={showModal} close={handleCloseModal} submit={formik.handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <CustomInput
            label="Categoría"
            error={
              formik.touched.name &&
              formik.errors.name &&
              Array.isArray(formik.errors.name)
                ? formik.errors.name.join(", ")
                : formik.errors.name
            }
            {...formik.getFieldProps("name")}
            onChange={(e) => {
              formik.handleChange(e);
            }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default CategoryPage;
